import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Space, Table, Tag, notification } from "antd";
import { Link } from "react-router-dom";
import { CloudUploadOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";

import SmartRtpLayout from "./components/SmartRtpLayout";
import SmartRtpCards from "./components/SmartRtpCards";
import { formatSmartRtpNumber } from "../../../utils/bigMath";
import { BIG_PRIZE_FILTER_TYPES } from "./config";
import {
  getBigPrizeBulkHistoryDetails,
  getBigPrizeBulkHistoryList,
  getBigPrizeDailySummary,
  getBigPrizeDaySummary,
} from "../../../controllers/V1/smartRtp/smartRtpController";

const FILTER_LABELS = Object.fromEntries(
  BIG_PRIZE_FILTER_TYPES.filter((item) => item.value).map((item) => [item.value, item.label])
);

function formatDateTime(value) {
  if (!value) return "-";
  return moment(value).format("DD-MM-YYYY HH:mm");
}

function formatDateOnly(value) {
  if (!value) return "-";
  return moment(value, "YYYY-MM-DD").format("DD-MM-YYYY");
}

function sumTableScrollX(columns = []) {
  return columns.reduce((total, col) => total + (Number(col.width) || 0), 0);
}

function useAntTableHeaderSync(deps = []) {
  const tableWrapRef = useRef(null);

  useLayoutEffect(() => {
    const wrapper = tableWrapRef.current;
    if (!wrapper) return undefined;

    const syncHeaderScrollbar = () => {
      const body = wrapper.querySelector(".ant-table-body");
      const header = wrapper.querySelector(".ant-table-header");
      if (!body || !header) return;
      const scrollbarWidth = body.offsetWidth - body.clientWidth;
      header.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "0px";
    };

    syncHeaderScrollbar();
    const body = wrapper.querySelector(".ant-table-body");
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncHeaderScrollbar) : null;
    if (body && observer) observer.observe(body);
    window.addEventListener("resize", syncHeaderScrollbar);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", syncHeaderScrollbar);
    };
  }, deps);

  return tableWrapRef;
}

const HISTORY_TABLE_WIDTHS = {
  dailyDate: 120,
  dailyAwards: 90,
  dailyAmount: 140,
  dailyPos: 100,
  savedAt: 170,
  source: 90,
  dateRange: 180,
  posRows: 90,
  games: 80,
  totalPrize: 120,
  savedBy: 200,
  action: 90,
  detailPos: 80,
  detailTime: 90,
  detailGamePrize: 112,
};

function prizeStatusTag(status) {
  const map = {
    "Winner Given": "success",
    Pending: "processing",
    Expired: "error",
  };
  return <Tag color={map[status] || "default"} style={{ marginTop: 4, marginInlineEnd: 0 }}>{status || "Pending"}</Tag>;
}

export default function SmartRtpBigPrizeBulkHistoryPage() {
  const [todaySummary, setTodaySummary] = useState({});
  const [dailyRows, setDailyRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const pageSize = 20;
  const dailyTableRef = useAntTableHeaderSync([dailyRows.length, loadingDaily]);
  const historyTableRef = useAntTableHeaderSync([historyRows.length, loadingHistory, historyPage]);
  const detailTableRef = useAntTableHeaderSync([
    selectedBatch?.rows?.length || 0,
    detailsLoading,
    detailsOpen,
    selectedBatch?.games_affected?.length || 0,
  ]);

  const loadTodaySummary = useCallback(async () => {
    const res = await getBigPrizeDaySummary({});
    if (res?.status === true) {
      setTodaySummary(res.result || {});
    } else {
      notification.error({
        message: "Could not load big prize summary",
        description: res?.message || "Please try again.",
      });
    }
  }, []);

  const loadDailySummary = useCallback(async () => {
    setLoadingDaily(true);
    const res = await getBigPrizeDailySummary({ days: 30, limit: 30 });
    if (res?.status === true) {
      setDailyRows(Array.isArray(res.result) ? res.result : []);
    } else {
      setDailyRows([]);
    }
    setLoadingDaily(false);
  }, []);

  const loadHistory = useCallback(async (page = 1) => {
    setLoadingHistory(true);
    const res = await getBigPrizeBulkHistoryList({
      skip: (page - 1) * pageSize,
      limit: pageSize,
      sort: { createdAt: -1 },
    });
    if (res?.status === true) {
      setHistoryRows(Array.isArray(res.result) ? res.result : []);
      setHistoryCount(res.count || 0);
    } else {
      setHistoryRows([]);
      setHistoryCount(0);
      notification.error({
        message: "Could not load upload history",
        description: res?.message || "Please try again.",
      });
    }
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    document.title = "Crown King || Attack Mode Upload History";
    loadTodaySummary();
    loadDailySummary();
    loadHistory(1);
  }, [loadTodaySummary, loadDailySummary, loadHistory]);

  const openDetails = async (record) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setSelectedBatch(null);
    const res = await getBigPrizeBulkHistoryDetails({ editId: record._id });
    if (res?.status === true) {
      setSelectedBatch(res.result || null);
    } else {
      notification.error({
        message: "Could not load batch details",
        description: res?.message || "Please try again.",
      });
      setDetailsOpen(false);
    }
    setDetailsLoading(false);
  };

  const summaryCards = [
    {
      key: "awards",
      label: "Big Prizes Awarded (Today)",
      value: todaySummary.total_awards || 0,
    },
    {
      key: "amount",
      label: "Big Prize Amount (Today)",
      value: formatSmartRtpNumber(todaySummary.total_amount || 0, 2),
    },
    {
      key: "pos",
      label: "POS With Big Prize (Today)",
      value: todaySummary.unique_pos || 0,
    },
  ];

  const dailyColumns = [
    {
      title: "Date",
      dataIndex: "filter_date",
      key: "filter_date",
      width: HISTORY_TABLE_WIDTHS.dailyDate,
      align: "left",
      render: (value) => formatDateOnly(value),
    },
    {
      title: "Awards",
      dataIndex: "total_awards",
      key: "total_awards",
      width: HISTORY_TABLE_WIDTHS.dailyAwards,
      align: "right",
    },
    {
      title: "Total Amount (QR)",
      dataIndex: "total_amount",
      key: "total_amount",
      width: HISTORY_TABLE_WIDTHS.dailyAmount,
      align: "right",
      render: (value) => formatSmartRtpNumber(value, 2),
    },
    {
      title: "Unique POS",
      dataIndex: "unique_pos",
      key: "unique_pos",
      width: HISTORY_TABLE_WIDTHS.dailyPos,
      align: "right",
    },
  ];

  const dailyScrollX = useMemo(() => sumTableScrollX(dailyColumns), [dailyColumns]);

  const historyColumns = [
    {
      title: "Saved At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: HISTORY_TABLE_WIDTHS.savedAt,
      align: "left",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: HISTORY_TABLE_WIDTHS.source,
      align: "left",
      render: (value) => (
        <Tag color={value === "excel" ? "blue" : "default"}>{value === "excel" ? "Excel" : "Manual"}</Tag>
      ),
    },
    {
      title: "Date Range",
      key: "date_range",
      width: HISTORY_TABLE_WIDTHS.dateRange,
      ellipsis: true,
      align: "left",
      render: (_, row) =>
        row.from_date && row.to_date
          ? `${formatDateOnly(row.from_date)} - ${formatDateOnly(row.to_date)}`
          : "-",
    },
    {
      title: "POS Rows",
      dataIndex: "pos_count",
      key: "pos_count",
      align: "right",
      width: HISTORY_TABLE_WIDTHS.posRows,
    },
    {
      title: "Games",
      dataIndex: "updated_games",
      key: "updated_games",
      align: "right",
      width: HISTORY_TABLE_WIDTHS.games,
    },
    {
      title: "Total Prize (QR)",
      dataIndex: "total_prize_amount",
      key: "total_prize_amount",
      width: HISTORY_TABLE_WIDTHS.totalPrize,
      align: "right",
      render: (value) => formatSmartRtpNumber(value, 2),
    },
    {
      title: "Saved By",
      dataIndex: "admin_email",
      key: "admin_email",
      width: HISTORY_TABLE_WIDTHS.savedBy,
      align: "left",
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "Action",
      key: "action",
      width: HISTORY_TABLE_WIDTHS.action,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => openDetails(record)}>
          View
        </Button>
      ),
    },
  ];

  const historyScrollX = useMemo(() => sumTableScrollX(historyColumns), [historyColumns]);

  const detailRowColumns = useMemo(() => {
    const gameTitles = (selectedBatch?.games_affected || []).map((game) => ({
      title: (
        <span
          style={{
            display: "inline-block",
            maxWidth: HISTORY_TABLE_WIDTHS.detailGamePrize - 16,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "bottom",
          }}
          title={game.game_title || game.game_id}
        >
          {game.game_title || game.game_id}
        </span>
      ),
      key: game.game_id,
      width: HISTORY_TABLE_WIDTHS.detailGamePrize,
      align: "right",
      render: (_, row) => {
        const prize = (row.prizes || []).find((item) => item.game_id === game.game_id);
        if (!prize) return "-";
        return (
          <div style={{ textAlign: "right" }}>
            <div>{formatSmartRtpNumber(prize.prize_amount, 2)}</div>
            {prizeStatusTag(prize.status)}
            {prize.order_no ? (
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Order {prize.order_no}</div>
            ) : null}
          </div>
        );
      },
    }));

    return [
      {
        title: "POS",
        dataIndex: "pos_number",
        key: "pos_number",
        width: HISTORY_TABLE_WIDTHS.detailPos,
        fixed: "left",
      },
      {
        title: "Start",
        dataIndex: "start_time",
        key: "start_time",
        width: HISTORY_TABLE_WIDTHS.detailTime,
        align: "center",
      },
      {
        title: "End",
        dataIndex: "end_time",
        key: "end_time",
        width: HISTORY_TABLE_WIDTHS.detailTime,
        align: "center",
      },
      ...gameTitles,
    ];
  }, [selectedBatch]);

  const detailScrollX = useMemo(() => sumTableScrollX(detailRowColumns), [detailRowColumns]);

  return (
    <SmartRtpLayout
      title="Attack Mode Upload History"
      activeKey="big-prize-monitor"
      actions={
        <Space wrap>
          <Link to="/smart-rtp/big-prize/addeditdata">
            <Button type="primary" icon={<CloudUploadOutlined />}>
              Bulk Upload
            </Button>
          </Link>
          <Link to="/smart-rtp/big-prize-monitor">
            <Button icon={<PlusOutlined />}>Attack Mode Monitor</Button>
          </Link>
        </Space>
      }
    >
      <style>{`
        .smart-rtp-aligned-table .ant-table-header {
          overflow: hidden !important;
        }
        .smart-rtp-aligned-table .ant-table-body {
          overflow: auto scroll !important;
        }
        .smart-rtp-aligned-table .ant-table-cell {
          overflow: hidden;
        }
      `}</style>
      <div className="card shadow mb-3">
        <div className="card-body">
          <h6 className="mb-3">Daily Attack Mode Summary</h6>
          <SmartRtpCards cards={summaryCards} />
          <div ref={dailyTableRef}>
            <Table
              className="smart-rtp-aligned-table"
              rowKey="filter_date"
              size="small"
              loading={loadingDaily}
              columns={dailyColumns}
              dataSource={dailyRows}
              tableLayout="fixed"
              pagination={false}
              scroll={{ x: dailyScrollX }}
            />
          </div>
        </div>
      </div>

      <div className="card shadow mb-3">
        <div className="card-body">
          <h6 className="mb-3">Saved Prize Upload History</h6>
          <div ref={historyTableRef}>
            <Table
              className="smart-rtp-aligned-table"
              rowKey="_id"
              size="small"
              loading={loadingHistory}
              columns={historyColumns}
              dataSource={historyRows}
              tableLayout="fixed"
              scroll={{ x: historyScrollX }}
              pagination={{
                current: historyPage,
                pageSize,
                total: historyCount,
                showSizeChanger: false,
                onChange: (page) => {
                  setHistoryPage(page);
                  loadHistory(page);
                },
              }}
            />
          </div>
        </div>
      </div>

      <Drawer
        title={selectedBatch?.batch_id || "Upload Details"}
        width={900}
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedBatch(null);
        }}
      >
        {selectedBatch ? (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <div><strong>Saved At:</strong> {formatDateTime(selectedBatch.createdAt)}</div>
              <div><strong>Source:</strong> {selectedBatch.source === "excel" ? "Excel Upload" : "Manual Grid Save"}</div>
              <div><strong>Filter:</strong> {FILTER_LABELS[selectedBatch.filter_type] || selectedBatch.filter_type || "-"}</div>
              <div>
                <strong>Date Range:</strong>{" "}
                {selectedBatch.from_date && selectedBatch.to_date
                  ? `${formatDateOnly(selectedBatch.from_date)} - ${formatDateOnly(selectedBatch.to_date)}`
                  : "-"}
              </div>
              <div><strong>File:</strong> {selectedBatch.file_name || "-"}</div>
              <div><strong>Saved By:</strong> {selectedBatch.admin_email || "-"}</div>
              <div><strong>Total Prize:</strong> {formatSmartRtpNumber(selectedBatch.total_prize_amount, 2)} QR</div>
              {selectedBatch.prize_date_key ? (
                <div><strong>Prize Day:</strong> {formatDateOnly(selectedBatch.prize_date_key)} (Bahrain)</div>
              ) : null}
            </div>

            <div ref={detailTableRef}>
              <Table
                className="smart-rtp-aligned-table"
                rowKey="pos_number"
                size="small"
                loading={detailsLoading}
                columns={detailRowColumns}
                dataSource={selectedBatch.rows || []}
                tableLayout="fixed"
                pagination={{ pageSize: 20 }}
                scroll={{ x: detailScrollX, y: 420 }}
              />
            </div>
          </Space>
        ) : null}
      </Drawer>
    </SmartRtpLayout>
  );
}
